import {
  authenticatedFetch,
  extractApiErrorMessage,
  resolvePeriverseApiBaseUrl,
} from '../shared';
import {
  AssetFilterParams,
  AssetResponseDto,
  AssetResponseDtoSchema,
  AssetHistoryQueryParams,
  PaginatedAssetHistoryResponseDto,
  PaginatedAssetHistoryResponseDtoSchema,
  PaginatedAssetResponseDto,
  PaginatedAssetResponseDtoSchema,
  CreateAssetDto,
  UpdateAssetDto,
} from '@peritasai/types';

type BaseUrlResolver = () => string;

export class AssetApiClient {
  constructor(
    private readonly resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
  ) {}

  async getAssets(
    options: AssetFilterParams = {}
  ): Promise<PaginatedAssetResponseDto> {
    const queryParams = new URLSearchParams();

    if (options.type) {
      queryParams.append('type', options.type);
    }
    if (options.search) {
      queryParams.append('search', options.search);
    }
    if (options.name) {
      queryParams.append('name', options.name);
    }
    if (options.locationId) {
      queryParams.append('locationId', options.locationId);
    }
    if (options.assetCatalogId) {
      queryParams.append('assetCatalogId', options.assetCatalogId);
    }
    if (options.status) {
      queryParams.append('status', options.status);
    }
    if (options.includeAssignedCase) {
      queryParams.append(
        'includeAssignedCase',
        options.includeAssignedCase.toString()
      );
    }

    // Handle pagination - API expects 1-based page, but DataGrid uses 0-based
    // Convert 0-based page to 1-based: page 0 -> 1, page 1 -> 2, etc.
    const page = options.page !== undefined ? options.page + 1 : 1;
    const limit = options.pageSize || 10;
    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());

    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/assets${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    try {
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(await extractApiErrorMessage(response));
      }

      return PaginatedAssetResponseDtoSchema.parse(await response.json());
    } catch (error) {
      console.error('Error fetching assets:', error);
      throw error;
    }
  }

  async getAssetById(id: string): Promise<AssetResponseDto> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/assets/${id}`;

    try {
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(await extractApiErrorMessage(response));
      }

      return AssetResponseDtoSchema.parse(await response.json());
    } catch (error) {
      console.error(`Error fetching asset ${id}:`, error);
      throw error;
    }
  }

  async getAssetHistory(
    id: string,
    params: AssetHistoryQueryParams
  ): Promise<PaginatedAssetHistoryResponseDto> {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);
    if (params.page !== undefined) {
      queryParams.append('page', params.page.toString());
    }
    if (params.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }

    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/assets/${id}/history${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    try {
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(await extractApiErrorMessage(response));
      }

      if (response.status === 204) {
        return {
          data: [],
          total: 0,
          page: params.page ?? 1,
          limit: params.limit ?? 50,
        };
      }

      return PaginatedAssetHistoryResponseDtoSchema.parse(
        await response.json()
      );
    } catch (error) {
      console.error(`Error fetching asset history for ${id}:`, error);
      throw error;
    }
  }

  async createAsset(dto: CreateAssetDto): Promise<AssetResponseDto> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/assets`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return AssetResponseDtoSchema.parse(await response.json());
  }

  async updateAsset(
    id: string,
    dto: UpdateAssetDto
  ): Promise<AssetResponseDto> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/assets/${id}`;

    const response = await authenticatedFetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return AssetResponseDtoSchema.parse(await response.json());
  }

}

export const createAssetApiClient = (
  resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
): AssetApiClient => new AssetApiClient(resolveBaseUrl);

export const assetApiClient = new AssetApiClient();

export type { BaseUrlResolver };
